import { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Edit3, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const FONT = { fontFamily: "'Crimson Text', Georgia, serif" };
const PASSWORD = "parker";

export const SpeakerNotes = () => {
  const [notes, setNotes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [pwVerified, setPwVerified] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwAction, setPwAction] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [form, setForm] = useState({ title: '', speaker_name: '', date: '', service_type: '', scripture: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    try { const r = await fetch(`${API}/api/speaker-notes`); if (r.ok) setNotes(await r.json()); } catch { toast.error('Unable to load notes'); }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const requirePw = (action) => {
    if (pwVerified) { action(); return; }
    setPwAction(() => action);
    setPwDialogOpen(true);
  };

  const handlePwSubmit = () => {
    if (pwInput.toLowerCase() === PASSWORD) {
      setPwVerified(true);
      setPwDialogOpen(false);
      if (pwAction) pwAction();
      setPwInput('');
    } else {
      toast.error('Invalid password');
    }
  };

  const openAdd = () => requirePw(() => {
    setEditNote(null);
    setForm({ title: '', speaker_name: '', date: '', service_type: '', scripture: '', notes: '' });
    setEditOpen(true);
  });

  const openEdit = (note) => requirePw(() => {
    setEditNote(note);
    setForm({ title: note.title || '', speaker_name: note.speaker_name, date: note.date, service_type: note.service_type, scripture: note.scripture, notes: note.notes });
    setEditOpen(true);
  });

  const saveNote = async () => {
    if (!form.speaker_name || !form.date) { toast.error('Speaker name and date are required'); return; }
    setSaving(true);
    try {
      const isNew = !editNote;
      const url = isNew ? `${API}/api/speaker-notes` : `${API}/api/speaker-notes/${editNote.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, password: PASSWORD }) });
      if (res.ok) { toast.success(isNew ? 'Note added!' : 'Note updated!'); fetchNotes(); setEditOpen(false); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const deleteNote = async (note) => {
    requirePw(async () => {
      try {
        const r = await fetch(`${API}/api/speaker-notes/${note.id}?password=${PASSWORD}`, { method: 'DELETE' });
        if (r.ok) { toast.success('Deleted'); fetchNotes(); } else toast.error('Failed');
      } catch { toast.error('Error'); }
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <BookOpen className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-4">Speaker Notes</h1>
          <p className="text-2xl text-yellow-300">Capturing the word as it is spoken</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-5xl font-bold text-blue-800">Notes</h2>
            <Button onClick={openAdd} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT} data-testid="add-note-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Note
            </Button>
          </div>

          {notes.length === 0 ? (
            <p className="text-blue-600 text-center py-12" style={FONT}>No speaker notes yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map(note => (
                <Card key={note.id} className="border-2 border-blue-200 hover:border-yellow-400 transition-colors">
                  <CardContent className="p-0">
                    <button onClick={() => setExpanded(expanded === note.id ? null : note.id)}
                      className="w-full text-left p-5 flex items-center justify-between" data-testid={`note-toggle-${note.id}`}>
                      <div>
                        {note.title && <p className="font-bold text-blue-900 text-xl" style={FONT}>{note.title}</p>}
                        <p className={`font-semibold text-blue-700 ${note.title ? 'text-base' : 'text-lg font-bold text-blue-800'}`} style={FONT}>{note.speaker_name}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="outline" className="border-blue-300 text-blue-600" style={FONT}>{note.date}</Badge>
                          {note.service_type && <Badge variant="outline" className="border-yellow-400 text-yellow-700" style={FONT}>{note.service_type}</Badge>}
                        </div>
                      </div>
                      {expanded === note.id ? <ChevronUp className="h-5 w-5 text-blue-400" /> : <ChevronDown className="h-5 w-5 text-blue-400" />}
                    </button>
                    {expanded === note.id && (
                      <div className="px-5 pb-5 border-t border-blue-100 pt-4 space-y-3">
                        {note.scripture && (
                          <div><span className="font-semibold text-yellow-700" style={FONT}>Scripture: </span>
                            <span className="text-blue-800" style={FONT}>{note.scripture}</span></div>
                        )}
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <pre className="whitespace-pre-wrap text-blue-900 text-base leading-relaxed" style={FONT}>{note.notes}</pre>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button onClick={() => openEdit(note)} variant="outline" className="border-blue-300 text-blue-700" style={FONT} data-testid={`edit-note-${note.id}`}>
                            <Edit3 className="h-4 w-4 mr-1" /> Edit
                          </Button>
                          <Button onClick={() => deleteNote(note)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" style={FONT} data-testid={`delete-note-${note.id}`}>
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Password Dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={o => { if (!o) { setPwDialogOpen(false); setPwInput(''); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="notes-pw-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-800">Speaker Notes Access</DialogTitle>
            <DialogDescription style={FONT}>Enter the password to continue.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input type="password" data-testid="notes-pw-input" value={pwInput} onChange={e => setPwInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePwSubmit()} placeholder="Password" className="border-2 border-blue-200" style={FONT} />
          </div>
          <DialogFooter>
            <Button onClick={handlePwSubmit} className="bg-blue-700 hover:bg-blue-800 text-white" style={FONT}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={editOpen} onOpenChange={o => !o && setEditOpen(false)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="note-edit-dialog">
          <DialogHeader><DialogTitle className="text-xl text-blue-800">{editNote ? 'Edit Note' : 'Add Note'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label style={FONT} className="text-blue-800 font-semibold">Title</Label>
              <Input data-testid="note-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. The Power of Faith" className="mt-1 border-2 border-blue-200" style={FONT} /></div>
            <div><Label style={FONT} className="text-blue-800 font-semibold">Speaker Name *</Label>
              <Input data-testid="note-speaker-input" value={form.speaker_name} onChange={e => setForm(f => ({ ...f, speaker_name: e.target.value }))} className="mt-1 border-2 border-blue-200" style={FONT} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label style={FONT} className="text-blue-800 font-semibold">Date *</Label>
                <Input type="date" data-testid="note-date-input" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1 border-2 border-blue-200" style={FONT} /></div>
              <div><Label style={FONT} className="text-blue-800 font-semibold">Service Type</Label>
                <Input data-testid="note-service-input" value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} placeholder="e.g. Sunday Service" className="mt-1 border-2 border-blue-200" style={FONT} /></div>
            </div>
            <div><Label style={FONT} className="text-blue-800 font-semibold">Scripture</Label>
              <Input value={form.scripture} onChange={e => setForm(f => ({ ...f, scripture: e.target.value }))} placeholder="e.g. John 3:16" className="mt-1 border-2 border-blue-200" style={FONT} /></div>
            <div><Label style={FONT} className="text-blue-800 font-semibold">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={8} placeholder="Sermon notes..." className="mt-1 border-2 border-blue-200" style={FONT} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="note-save-btn" onClick={saveNote} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
